/*
 * Payroll (draft 4): what each person is owed for a pay week, worked out again from the paid visits and the shifts every
 * time. BUILD-SPEC 3.8 is the law; the WordPress Steward's payroll.php is the spec where the law is silent. Pure functions
 * only, so the server, the web screens, the preview and the phone app all come to the same cents.
 *
 * Three layers:
 * 1. rawWeek: one week from the work paid in it and the shifts started in it, with the pay settings given.
 * 2. Pay runs: marking a week paid keeps a snapshot (PaidRun). A paid week is frozen; anything that changes after (a late
 *    job, a tip, a changed crew, a fixed clock) is a row in the first unpaid week after it (lateRows), counted once.
 * 3. payWeek: a week as the screens show it, with what is owed from earlier weeks and the rows from paid weeks.
 *
 * Weeks run Monday to Sunday from PAY_WEEK_ONE, in the company's time zone (the caller gives local dates). A company may pay
 * every two weeks instead (PayCalendar, owner 2026-10-28, question 47): periods of 14 days from its first two-week Monday,
 * with weekly periods before that day. The functions still say "week" for a pay period of either length.
 */
import { splitCents, type Cents } from "./core.js";
import { addDays } from "./schedule.js";

/** The Monday every pay week is counted from (the WordPress Steward's STEWARD_WEEK_ONE). */
export const PAY_WEEK_ONE = "2026-01-05";
/** How far back money owed and changes to paid weeks are looked for. */
export const LOOK_BACK_WEEKS = 12;
/** A shift is at most 16 hours, so a forgotten clock out does not run up days of time. */
export const MAX_SHIFT_MINUTES = 16 * 60;
/** A "bills every 4 visits" invoice pays its four visits in four equal parts. */
export const MONTHLY_PARTS = 4;

// Days -----------------------------------------------------------------------------------------------------------

const DAY_MS = 86_400_000;
const dayMs = (d: string) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));

/** The Monday that starts the pay week a day falls in. */
export function weekOf(date: string): string {
  const days = Math.round((dayMs(date) - dayMs(PAY_WEEK_ONE)) / DAY_MS);
  return addDays(PAY_WEEK_ONE, Math.floor(days / 7) * 7);
}

export const isWeekStart = (date: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(date) && weekOf(date) === date;
export const weekEnd = (start: string): string => addDays(start, 6);

/**
 * How long pay periods are. weeks 2: every two weeks from `from` (a Monday); the weeks before it stay weekly, so a company
 * that paid weekly can move to two-week pay from a day after its last paid week. weeks 1: weekly (`from` is not used).
 */
export interface PayCalendar {
  weeks: 1 | 2;
  from: string;
}

export const WEEKLY: PayCalendar = { weeks: 1, from: PAY_WEEK_ONE };

/** The calendar from the payroll settings: two-week pay from its first Monday (Jan 5 2026 when not set). */
export function payCalendar(weeks: number, from: string | null | undefined): PayCalendar {
  return weeks === 2 ? { weeks: 2, from: from && isWeekStart(from) ? from : PAY_WEEK_ONE } : WEEKLY;
}

const twoWeekly = (date: string, cal: PayCalendar) => cal.weeks === 2 && date >= cal.from;

/** The first day of the pay period a day falls in. */
export function periodOf(date: string, cal: PayCalendar = WEEKLY): string {
  if (!twoWeekly(date, cal)) return weekOf(date);
  const days = Math.round((dayMs(date) - dayMs(cal.from)) / DAY_MS);
  return addDays(cal.from, Math.floor(days / 14) * 14);
}

export const isPeriodStart = (date: string, cal: PayCalendar = WEEKLY): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(date) && periodOf(date, cal) === date;

/** The first day of the period after the one starting on start. */
export const nextPeriod = (start: string, cal: PayCalendar = WEEKLY): string => addDays(start, twoWeekly(start, cal) ? 14 : 7);

/** The last day of the period starting on start. */
export const periodEnd = (start: string, cal: PayCalendar = WEEKLY): string => addDays(nextPeriod(start, cal), -1);

/** The day a period is paid: the first paydayWeekday (1 Monday to 7 Sunday) after it ends. */
export function paydayOf(start: string, paydayWeekday: number, cal: PayCalendar = WEEKLY): string {
  return addDays(periodEnd(start, cal), paydayWeekday);
}

/** The period has not ended on today, so marking it paid now is paying early (owner, question 48). */
export const isEarly = (start: string, today: string, cal: PayCalendar = WEEKLY): boolean => periodEnd(start, cal) >= today;

/**
 * The period the Payroll page opens on (owner, 2026-11-02): the one that ended last, so the week paid this payday stays up
 * until the week it is paid in is over. Then the next one. Weekly pay always opens on last week; two-week pay opens on the
 * period before during the first week of a new one.
 */
export function periodToShow(today: string, cal: PayCalendar = WEEKLY): string {
  const cur = periodOf(today, cal);
  const before = periodOf(addDays(cur, -1), cal);
  return today <= addDays(periodEnd(before, cal), 7) ? before : cur;
}

// Money ----------------------------------------------------------------------------------------------------------

/** A percent of an amount in cents, half a cent rounded away from zero, in whole numbers only (payroll.php percent_cents). */
export function poolCents(baseCents: number, percent: number): number {
  const x = baseCents * Math.round(percent * 100);
  const r = Math.floor((Math.abs(x) + 5000) / 10000);
  return x < 0 ? -r : r;
}

/**
 * The pool of a visit: the highest percent among the percent-paid people on it. A pool set on the visit or its job raises
 * it (never below that highest percent); with none set, the company's default pool raises it the same way. Nobody with a
 * percent on the visit means no pool (the hourly people are paid by their hours).
 *
 * A commercial visit (the client is a business) pays the company's commercial pool instead, whatever the people's own
 * percents (owner, 2026-11-02: "commercial does have a set pool and its 50%"). A pool set by hand still raises it.
 */
export function visitPool(
  weights: readonly number[],
  ownPercent: number | null,
  defaultPercent: number | null,
  commercialPercent: number | null = null,
): { percent: number; raised: boolean } {
  const top = weights.length ? Math.max(...weights) : 0;
  const manual = ownPercent && ownPercent > 0 ? Math.min(100, ownPercent) : 0;
  if (top > 0 && commercialPercent !== null && commercialPercent > 0) {
    const set = Math.min(100, commercialPercent);
    return { percent: Math.max(set, manual), raised: manual > set };
  }
  const percent = manual > 0 ? Math.max(top, manual) : top > 0 ? Math.max(top, defaultPercent ?? 0) : 0;
  return { percent, raised: manual > 0 && manual >= top };
}

/** A 4-visit invoice's price in four equal parts, the odd cents to the first visits. */
export function monthlyParts(baseCents: number): number[] {
  return splitCents(
    baseCents as Cents,
    Array.from({ length: MONTHLY_PARTS }, () => 1),
  );
}

const evenSplit = (cents: number, n: number): number[] =>
  n > 0
    ? splitCents(
        cents as Cents,
        Array.from({ length: n }, () => 1),
      )
    : [];

// Inputs ---------------------------------------------------------------------------------------------------------

export type PayType = "percent" | "hourly";

/** How one person is paid, as kept with a paid week so a new percent or rate never reaches back into it. */
export interface PayPerson {
  payType: PayType;
  percent: number | null;
  hourlyRateCents: number | null;
  active: boolean;
}

export interface PaySettings {
  defaultPoolPercent: number | null;
  /** The set pool on commercial visits. Left out (weeks paid before it existed) or null: commercial pays like any job. */
  commercialPoolPercent?: number | null;
  /** By person id, in the order people are listed. */
  people: Record<string, PayPerson>;
}

/**
 * Money that came in for a visit on one day: the price of its work (before tips) when its invoice was paid, and tips when
 * they were paid. A visit paid on one day with a tip on another is two of these.
 */
export interface PaidWork {
  visitId: string;
  /** The visit's service date. */
  date: string;
  clientName: string;
  /** The company-local day the money came in. */
  on: string;
  baseCents: number;
  tipCents: number;
  /** Person ids. Empty: nobody is set on it yet. */
  crew: readonly string[];
  /** The visit's own pool, else its job's. */
  poolPercent: number | null;
  /** The crew kept the client's cash or check. */
  crewCollected: boolean;
  /** The client is a business, so the commercial pool applies. */
  commercial?: boolean;
  /**
   * How tipCents is shared, by person, when it is not shared evenly by this visit's crew: a tip on an invoice is shared by
   * everyone on the invoice (invoiceTipParts). Whatever is not named here is shared evenly by the visit's crew.
   */
  tipSplit?: Readonly<Record<string, number>>;
  /** Work on an invoice typed by hand: visitId is the invoice's id (owner, question 43). */
  byHand?: boolean;
}

/**
 * A tip on an invoice, shared evenly by everyone on it (every visit's crew, each person once; owner, question 45). Each
 * person's share rides on the first visit they worked. Nobody on any visit: the whole tip is on the first visit.
 */
export function invoiceTipParts(
  visits: readonly { crew: readonly string[] }[],
  tipCents: number,
): { cents: number; split: Record<string, number> }[] {
  const out = visits.map((): { cents: number; split: Record<string, number> } => ({ cents: 0, split: {} }));
  const firstOn = new Map<string, number>();
  visits.forEach((v, i) => {
    for (const id of v.crew) if (!firstOn.has(id)) firstOn.set(id, i);
  });
  if (!out.length) return out;
  if (!firstOn.size) {
    out[0]!.cents = tipCents;
    return out;
  }
  const ids = [...firstOn.keys()];
  evenSplit(tipCents, ids.length).forEach((c, k) => {
    const id = ids[k]!;
    const part = out[firstOn.get(id)!]!;
    part.cents += c;
    part.split[id] = (part.split[id] ?? 0) + c;
  });
  return out;
}

export interface Shift {
  id: string;
  personId: string;
  /** The company-local day it started. A shift past midnight stays one shift on that day. */
  date: string;
  clockIn: number;
  clockOut: number | null;
}

/** Done visits that are not paid yet, for Waiting on payment and If everyone paid. */
export interface WaitingWork {
  visitId: string;
  date: string;
  clientName: string;
  baseCents: number;
  crew: readonly string[];
  poolPercent: number | null;
  commercial?: boolean;
  byHand?: boolean;
}

/** Minutes on a shift. One still open counts up to now. Either way at most 16 hours. */
export function shiftMinutes(s: Pick<Shift, "clockIn" | "clockOut">, now: number): number {
  const m = Math.round(((s.clockOut ?? now) - s.clockIn) / 60_000);
  return Math.max(0, Math.min(MAX_SHIFT_MINUTES, m));
}

/** Where a clock out lands: never before the clock in, never more than 16 hours after it. */
export function capClockOut(clockIn: number, at: number): number {
  return Math.min(Math.max(at, clockIn), clockIn + MAX_SHIFT_MINUTES * 60_000);
}

// 1. One week ----------------------------------------------------------------------------------------------------

/**
 * One person's part of a paid visit. payCents is what the visit adds to their pay: their share of the pool less the cash
 * they kept (heldCents). For someone paid by the hour it is only minus the cash kept, since their hours pay them.
 */
export interface JobShare {
  personId: string;
  payCents: number;
  tipCents: number;
  heldCents: number;
}

export interface PayJob {
  visitId: string;
  date: string;
  paidOn: string;
  clientName: string;
  baseCents: number;
  tipCents: number;
  poolPercent: number;
  /** Raised by hand on the visit or job. */
  raised: boolean;
  /** The crew kept the client's cash or check. */
  kept: boolean;
  /** The client is a business, so it paid the commercial pool. */
  commercial?: boolean;
  /** The pool in cents, before cash kept comes off. */
  poolPayCents: number;
  shares: JobShare[];
  /** Work on an invoice typed by hand: visitId is the invoice's id. */
  byHand?: boolean;
}

export interface NoCrewJob {
  visitId: string;
  date: string;
  paidOn: string;
  clientName: string;
  baseCents: number;
  tipCents: number;
  /** For work from a paid week: the week it came from. */
  fromStart: string | null;
  byHand?: boolean;
}

export interface PersonWeek {
  personId: string;
  payType: PayType;
  /** Visits with a price on them. */
  jobs: number;
  /** Sum of their job pay (for hourly people only the cash they kept, as a minus). */
  jobPayCents: number;
  tipCents: number;
  /** Client money they kept that came off. */
  heldCents: number;
  /** Hours times rate; null when no rate is set. Only for people paid by the hour. */
  hourlyPayCents: number | null;
  minutes: number;
  active: boolean;
  /** What this week's own work comes to: job pay + tips + hourly pay. */
  ownCents: number;
}

export interface RawWeek {
  start: string;
  end: string;
  people: PersonWeek[];
  jobs: PayJob[];
  noCrew: NoCrewJob[];
  totalCents: number;
}

const byPaid = <T extends { paidOn: string; date: string; visitId: string }>(a: T, b: T) =>
  a.paidOn.localeCompare(b.paidOn) || a.date.localeCompare(b.date) || a.visitId.localeCompare(b.visitId);

const personOf = (settings: PaySettings, id: string): PayPerson =>
  settings.people[id] ?? { payType: "percent", percent: null, hourlyRateCents: null, active: false };

const weightOf = (p: PayPerson) => (p.payType === "hourly" ? 0 : Math.max(0, p.percent ?? 0));

/** Shares out one visit's money between its crew (spec 3.8 steps 1 to 6). */
export function shareVisit(
  v: Pick<PaidWork, "baseCents" | "tipCents" | "crew" | "poolPercent" | "crewCollected" | "tipSplit" | "commercial">,
  settings: PaySettings,
): { poolPercent: number; raised: boolean; poolPayCents: number; shares: JobShare[] } {
  const crew = [...new Set(v.crew)];
  const w = crew.map((id) => weightOf(personOf(settings, id)));
  const commercial = v.commercial ? (settings.commercialPoolPercent ?? null) : null;
  const pool = visitPool(w, v.poolPercent, settings.defaultPoolPercent, commercial);
  const poolPay = pool.percent > 0 ? poolCents(v.baseCents, pool.percent) : 0;
  const parts = crew.length ? splitCents(poolPay as Cents, w) : [];
  // What the crew holds is the whole price; it comes off split the same way their share is.
  const held = v.crewCollected && crew.length ? splitCents(v.baseCents as Cents, w) : crew.map(() => 0);
  // Tip shares the invoice names go to those people; the rest is shared evenly by this visit's crew.
  const named = new Map<string, number>();
  for (const [pid, c] of Object.entries(v.tipSplit ?? {})) if (crew.includes(pid)) named.set(pid, c);
  const even = evenSplit(v.tipCents - [...named.values()].reduce((a, c) => a + c, 0), crew.length);
  const tips = crew.map((pid, i) => (named.get(pid) ?? 0) + (even[i] ?? 0));
  const shares = crew.map((personId, i) => {
    const hourly = personOf(settings, personId).payType === "hourly";
    const h = held[i] ?? 0;
    return { personId, payCents: (hourly ? 0 : (parts[i] ?? 0)) - h, tipCents: tips[i] ?? 0, heldCents: h };
  });
  return { poolPercent: pool.percent, raised: pool.raised, poolPayCents: poolPay, shares };
}

/** A week worked out from its own work only: the visits paid in it, the shifts started in it. */
export function rawWeek(
  start: string,
  work: readonly PaidWork[],
  shifts: readonly Shift[],
  settings: PaySettings,
  now: number,
  end: string = weekEnd(start),
): RawWeek {
  const inside = (d: string) => d >= start && d <= end;

  // The money that came in this week, per visit.
  const byVisit = new Map<string, PaidWork & { paidOn: string }>();
  for (const x of work) {
    if (!inside(x.on)) continue;
    const had = byVisit.get(x.visitId);
    if (had) {
      had.baseCents += x.baseCents;
      had.tipCents += x.tipCents;
      const into: Record<string, number> = { ...had.tipSplit };
      for (const [pid, c] of Object.entries(x.tipSplit ?? {})) into[pid] = (into[pid] ?? 0) + c;
      had.tipSplit = into;
      if (x.on < had.paidOn) had.paidOn = x.on;
    } else byVisit.set(x.visitId, { ...x, tipSplit: { ...x.tipSplit }, paidOn: x.on });
  }

  const jobs: PayJob[] = [];
  const noCrew: NoCrewJob[] = [];
  for (const v of byVisit.values()) {
    if (v.baseCents === 0 && v.tipCents === 0) continue;
    const head = {
      visitId: v.visitId,
      date: v.date,
      paidOn: v.paidOn,
      clientName: v.clientName,
      baseCents: v.baseCents,
      tipCents: v.tipCents,
      ...(v.byHand ? { byHand: true } : {}),
    };
    if (!v.crew.length) {
      noCrew.push({ ...head, fromStart: null });
      continue;
    }
    jobs.push({ ...head, kept: v.crewCollected, ...(v.commercial ? { commercial: true } : {}), ...shareVisit(v, settings) });
  }
  jobs.sort(byPaid);
  noCrew.sort(byPaid);

  const minutes = new Map<string, number>();
  for (const s of shifts) {
    if (!inside(s.date)) continue;
    minutes.set(s.personId, (minutes.get(s.personId) ?? 0) + shiftMinutes(s, now));
  }

  const sums = new Map<string, { jobs: number; pay: number; tips: number; held: number }>();
  for (const j of jobs) {
    for (const s of j.shares) {
      const m = sums.get(s.personId) ?? { jobs: 0, pay: 0, tips: 0, held: 0 };
      if (j.baseCents !== 0) m.jobs += 1;
      m.pay += s.payCents;
      m.tips += s.tipCents;
      m.held += s.heldCents;
      sums.set(s.personId, m);
    }
  }

  const people: PersonWeek[] = [];
  const ids = [...new Set([...Object.keys(settings.people), ...sums.keys(), ...minutes.keys()])];
  for (const personId of ids) {
    const p = personOf(settings, personId);
    const s = sums.get(personId) ?? { jobs: 0, pay: 0, tips: 0, held: 0 };
    const min = minutes.get(personId) ?? 0;
    const hourly = p.payType === "hourly";
    if (!s.jobs && !s.pay && !s.tips && !(min > 0 && (p.active || hourly))) continue;
    const rate = p.hourlyRateCents ?? 0;
    const hourlyPay = hourly ? (rate > 0 ? Math.round((min * rate) / 60) : null) : null;
    people.push({
      personId,
      payType: p.payType,
      jobs: s.jobs,
      jobPayCents: s.pay,
      tipCents: s.tips,
      heldCents: s.held,
      hourlyPayCents: hourlyPay,
      minutes: min,
      active: p.active,
      ownCents: s.pay + s.tips + (hourlyPay ?? 0),
    });
  }
  return { start, end, people, jobs, noCrew, totalCents: people.reduce((a, p) => a + p.ownCents, 0) };
}

/** Waiting on payment, and what each person's share would be if every one of them were paid (owner only). */
export function ifEveryonePaid(waiting: readonly WaitingWork[], settings: PaySettings) {
  const people = new Map<string, { jobs: number; payCents: number }>();
  let baseCents = 0;
  let noCrewCents = 0;
  for (const v of waiting) {
    baseCents += v.baseCents;
    if (!v.crew.length) {
      noCrewCents += v.baseCents;
      continue;
    }
    const s = shareVisit({ ...v, tipCents: 0, crewCollected: false }, settings);
    for (const x of s.shares) {
      const m = people.get(x.personId) ?? { jobs: 0, payCents: 0 };
      m.jobs += 1;
      m.payCents += x.payCents;
      people.set(x.personId, m);
    }
  }
  return {
    count: waiting.length,
    baseCents,
    noCrewCents,
    people: [...people].map(([personId, m]) => ({
      personId,
      jobs: m.jobs,
      payCents: m.payCents,
      hourly: personOf(settings, personId).payType === "hourly",
    })),
  };
}

// 2. Paid weeks --------------------------------------------------------------------------------------------------

/**
 * Work from a paid week that its pay run did not pay, landing in a later week. late: a visit the run did not have.
 * change: a visit it had, now different (a tip, the price, who worked it, cash kept). hours: hourly pay changed.
 */
export interface LateRow {
  kind: "late" | "change" | "hours";
  /** The paid week it came from. */
  from: string;
  visitId: string | null;
  personId: string;
  payCents: number;
  tipCents: number;
  hoursCents: number;
  date: string | null;
  clientName: string | null;
  cash: boolean;
  byHand?: boolean;
  /**
   * A week paid in the WordPress Steward before it kept a visit list (the copy, draft 15): this row, in the week's own
   * run, makes the run total what the old Steward paid. What the week comes to beyond that comes forward to the first
   * unpaid week after it, as the old Steward carried it (its carryChange).
   */
  carriesOn?: boolean;
}

/** What marking a week paid keeps. The week shows exactly this from then on. */
export interface PaidRun {
  start: string;
  paidAt: string;
  paidBy: string | null;
  /** The pay settings it was paid with. */
  settings: PaySettings;
  jobs: PayJob[];
  /** Hourly pay per person, in cents. */
  hours: Record<string, number>;
  minutes: Record<string, number>;
  /** Rows it took in from earlier paid weeks. */
  late: LateRow[];
  /** What each person owed from earlier weeks when it was paid (minus amounts). */
  owed: Record<string, { cents: number; from: string }>;
}

/** The words on the row that brings forward work that came in after the old Steward paid its week. */
export const CAME_IN_AFTER = "Came in after the old Steward paid";

const rowCents = (r: LateRow) => r.payCents + r.tipCents + r.hoursCents;
const add = (m: Map<string, number>, k: string, c: number) => m.set(k, (m.get(k) ?? 0) + c);

/** What each person was paid in a run (minus: they owed it, and it carried on). */
export function runTotals(run: PaidRun): Map<string, number> {
  const out = new Map<string, number>();
  for (const j of run.jobs) for (const s of j.shares) add(out, s.personId, s.payCents + s.tipCents);
  for (const [pid, c] of Object.entries(run.hours)) add(out, pid, c);
  for (const [pid] of Object.entries(run.minutes)) if (!out.has(pid)) out.set(pid, 0);
  for (const r of run.late) add(out, r.personId, rowCents(r));
  for (const [pid, o] of Object.entries(run.owed)) add(out, pid, o.cents);
  return out;
}

const hourlyOf = (raw: RawWeek) => {
  const out = new Map<string, number>();
  for (const p of raw.people) if (p.payType === "hourly" && p.hourlyPayCents) out.set(p.personId, p.hourlyPayCents);
  return out;
};

/** What a late row says about its visit. */
interface Meta {
  date: string | null;
  clientName: string | null;
  cash: boolean;
  byHand?: boolean | undefined;
}

/**
 * A paid week's rows for the first unpaid week after it: what the week comes to now (raw, worked out with the settings it
 * was paid with), less what its run paid, less what a later paid run already took in from it. So each change shows once.
 */
export function lateRows(run: PaidRun, raw: RawWeek, runs: Iterable<PaidRun>): LateRow[] {
  const rec = new Map<string, Map<string, [number, number]>>();
  const meta = new Map<string, Meta>();
  const recH = new Map<string, number>(Object.entries(run.hours));
  // What the old Steward's pay of this week still brings forward, by person, less what later paid weeks took in.
  const forward = new Map<string, number>();
  for (const L of run.late) if (L.carriesOn && L.from === run.start) add(forward, L.personId, -rowCents(L));
  const put = (vid: string, pid: string, pay: number, tip: number) => {
    const m = rec.get(vid) ?? new Map<string, [number, number]>();
    const was = m.get(pid) ?? [0, 0];
    m.set(pid, [was[0] + pay, was[1] + tip]);
    rec.set(vid, m);
  };
  for (const j of run.jobs) {
    meta.set(j.visitId, { date: j.date, clientName: j.clientName, cash: j.kept, byHand: j.byHand });
    if (!rec.has(j.visitId)) rec.set(j.visitId, new Map());
    for (const s of j.shares) put(j.visitId, s.personId, s.payCents, s.tipCents);
  }
  for (const r2 of runs) {
    if (r2.start === run.start) continue;
    for (const L of r2.late) {
      if (L.from !== run.start) continue;
      if (L.visitId === null) {
        if (L.kind === "hours") add(recH, L.personId, L.hoursCents);
        else add(forward, L.personId, -rowCents(L));
        continue;
      }
      put(L.visitId, L.personId, L.payCents, L.tipCents);
      if (!meta.has(L.visitId)) meta.set(L.visitId, { date: L.date, clientName: L.clientName, cash: L.cash, byHand: L.byHand });
    }
  }
  const live = new Map<string, Map<string, [number, number]>>();
  const liveMeta = new Map<string, Meta>();
  for (const j of raw.jobs) {
    liveMeta.set(j.visitId, { date: j.date, clientName: j.clientName, cash: j.kept, byHand: j.byHand });
    const m = new Map<string, [number, number]>();
    for (const s of j.shares) m.set(s.personId, [s.payCents, s.tipCents]);
    live.set(j.visitId, m);
  }
  const rows: LateRow[] = [];
  for (const vid of new Set([...live.keys(), ...rec.keys()])) {
    const a = live.get(vid) ?? new Map<string, [number, number]>();
    const b = rec.get(vid) ?? new Map<string, [number, number]>();
    for (const pid of new Set([...a.keys(), ...b.keys()])) {
      const [lp, lt] = a.get(pid) ?? [0, 0];
      const [rp, rt] = b.get(pid) ?? [0, 0];
      if (lp === rp && lt === rt) continue;
      const m: Meta = liveMeta.get(vid) ?? meta.get(vid) ?? { date: null, clientName: null, cash: false };
      rows.push({
        kind: meta.has(vid) ? "change" : "late",
        from: run.start,
        visitId: vid,
        personId: pid,
        payCents: lp - rp,
        tipCents: lt - rt,
        hoursCents: 0,
        date: m.date,
        clientName: m.clientName,
        cash: m.cash,
        ...(m.byHand ? { byHand: true } : {}),
      });
    }
  }
  const liveH = hourlyOf(raw);
  for (const pid of new Set([...liveH.keys(), ...recH.keys()])) {
    const d = (liveH.get(pid) ?? 0) - (recH.get(pid) ?? 0);
    if (d !== 0)
      rows.push({
        kind: "hours",
        from: run.start,
        visitId: null,
        personId: pid,
        payCents: 0,
        tipCents: 0,
        hoursCents: d,
        date: null,
        clientName: null,
        cash: false,
      });
  }
  for (const [pid, c] of forward)
    if (c !== 0)
      rows.push({
        kind: "change",
        from: run.start,
        visitId: null,
        personId: pid,
        payCents: c,
        tipCents: 0,
        hoursCents: 0,
        date: null,
        clientName: CAME_IN_AFTER,
        cash: false,
      });
  return rows.sort(
    (x, y) =>
      Number(x.kind === "hours") - Number(y.kind === "hours") ||
      (x.date ?? "").localeCompare(y.date ?? "") ||
      (x.visitId ?? "").localeCompare(y.visitId ?? "") ||
      x.personId.localeCompare(y.personId),
  );
}

/** A later paid week that took money from this one (rows from it, or money owed from it on), or null. Undo it first. */
export function undoBlockedBy(runs: Iterable<PaidRun>, start: string): string | null {
  for (const r of [...runs].sort((a, b) => a.start.localeCompare(b.start))) {
    if (r.start <= start) continue;
    if (Object.values(r.owed).some((o) => o.from >= start)) return r.start;
    if (r.late.some((L) => L.from === start)) return r.start;
  }
  return null;
}

// 3. A week as the screens show it -------------------------------------------------------------------------------

/** Everything payroll reads for a stretch of weeks. */
export interface PayBook {
  work: readonly PaidWork[];
  shifts: readonly Shift[];
  /** Weeks marked paid (and not undone), by their Monday. */
  runs: ReadonlyMap<string, PaidRun>;
  /** The pay settings now. */
  settings: PaySettings;
  paydayWeekday: number;
  /** How long pay periods are. Weekly when not given. */
  calendar?: PayCalendar;
  now: number;
}

export interface PayLine extends PersonWeek {
  /** Owed from earlier weeks (a minus amount), and the week it came from. */
  carryCents: number;
  carryFrom: string | null;
  /** Rows from paid weeks, added up. */
  lateCents: number;
  totalCents: number;
}

export interface PayWeekView {
  start: string;
  end: string;
  payday: string;
  /** Set when the week is marked paid: it shows its snapshot, and later work goes to nextStart. */
  paid: { paidAt: string; paidBy: string | null; nextStart: string } | null;
  people: PayLine[];
  jobs: PayJob[];
  late: LateRow[];
  noCrew: NoCrewJob[];
  totalCents: number;
}

/** Works out weeks from a book, each raw week once. */
export class Payroll {
  private readonly raws = new Map<string, RawWeek>();

  readonly cal: PayCalendar;

  constructor(readonly book: PayBook) {
    this.cal = book.calendar ?? WEEKLY;
  }

  raw(start: string, settings: PaySettings = this.book.settings): RawWeek {
    const key = `${start}|${settings === this.book.settings ? "now" : "run"}`;
    let r = this.raws.get(key);
    if (!r) {
      r = rawWeek(start, this.book.work, this.book.shifts, settings, this.book.now, periodEnd(start, this.cal));
      this.raws.set(key, r);
    }
    return r;
  }

  /** The first week after this one that is not marked paid: where work done after it was paid goes. */
  nextUnpaid(start: string): string {
    let q = nextPeriod(start, this.cal);
    for (let i = 0; i < 520 && this.book.runs.has(q); i += 1) q = nextPeriod(q, this.cal);
    return q;
  }

  /**
   * Walks the weeks before start, oldest first, and answers what lands in it: what each person owes from earlier weeks
   * (a paid week passes on only what it left below zero; an unpaid week passes on its total when that is below zero), the
   * rows of paid weeks whose first unpaid week after them is this one, and their paid jobs nobody was set on.
   */
  private walk(start: string) {
    let back = addDays(start, -7 * LOOK_BACK_WEEKS);
    if (back < PAY_WEEK_ONE) back = PAY_WEEK_ONE;
    const from = periodOf(back, this.cal);
    let carry = new Map<string, { cents: number; from: string }>();
    let open: { run: PaidRun; raw: RawWeek }[] = [];
    const rowsOf = (list: typeof open) => list.flatMap((o) => lateRows(o.run, o.raw, this.book.runs.values()));
    for (let p = from; p < start; p = nextPeriod(p, this.cal)) {
      const run = this.book.runs.get(p);
      const raw = this.raw(p, run?.settings);
      const next = new Map<string, { cents: number; from: string }>();
      if (run) {
        for (const [pid, c] of runTotals(run)) if (c < 0) next.set(pid, { cents: c, from: p });
        open.push({ run, raw });
      } else {
        const live = new Map<string, number>(raw.people.map((x) => [x.personId, x.ownCents]));
        for (const r of rowsOf(open)) add(live, r.personId, rowCents(r));
        open = [];
        for (const [pid, c] of carry) add(live, pid, c.cents);
        for (const [pid, c] of live) if (c < 0) next.set(pid, { cents: c, from: p });
      }
      carry = next;
    }
    return {
      carry,
      late: rowsOf(open),
      noCrew: open.flatMap((o) => o.raw.noCrew.map((j) => ({ ...j, fromStart: o.run.start }))),
    };
  }

  week(start: string): PayWeekView {
    const run = this.book.runs.get(start);
    const end = periodEnd(start, this.cal);
    const payday = paydayOf(start, this.book.paydayWeekday, this.cal);
    if (run) return { ...frozen(run), start, end, payday, paid: { ...paidOf(run), nextStart: this.nextUnpaid(start) } };
    const raw = this.raw(start);
    const w = this.walk(start);
    const lines = new Map<string, PayLine>(
      raw.people.map((p) => [p.personId, { ...p, carryCents: 0, carryFrom: null, lateCents: 0, totalCents: p.ownCents }]),
    );
    const line = (pid: string) => {
      let l = lines.get(pid);
      if (!l) {
        const p = personOf(this.book.settings, pid);
        l = { ...blank(pid, p), carryCents: 0, carryFrom: null, lateCents: 0, totalCents: 0 };
        lines.set(pid, l);
      }
      return l;
    };
    for (const [pid, c] of w.carry) {
      const l = line(pid);
      l.carryCents = c.cents;
      l.carryFrom = c.from;
      l.totalCents += c.cents;
    }
    for (const r of w.late) {
      const l = line(r.personId);
      l.lateCents += rowCents(r);
      l.totalCents += rowCents(r);
    }
    const people = [...lines.values()];
    return {
      start,
      end,
      payday,
      paid: null,
      people,
      jobs: raw.jobs,
      late: w.late,
      noCrew: [...raw.noCrew, ...w.noCrew].sort(byPaid),
      totalCents: people.reduce((a, p) => a + p.totalCents, 0),
    };
  }

  /** What marking an unpaid week paid keeps: its own work, the rows it takes in and what is owed into it. */
  snapshot(start: string, paidAt: string, paidBy: string | null): PaidRun {
    if (this.book.runs.has(start)) throw new Error("That week is already paid.");
    const raw = this.raw(start);
    const w = this.walk(start);
    const minutes: Record<string, number> = {};
    for (const p of raw.people) if (p.minutes > 0) minutes[p.personId] = p.minutes;
    const owed: PaidRun["owed"] = {};
    for (const [pid, c] of w.carry) if (c.cents !== 0) owed[pid] = c;
    return {
      start,
      paidAt,
      paidBy,
      settings: this.book.settings,
      jobs: raw.jobs,
      hours: Object.fromEntries(hourlyOf(raw)),
      minutes,
      late: w.late,
      owed,
    };
  }
}

const paidOf = (run: PaidRun) => ({ paidAt: run.paidAt, paidBy: run.paidBy });

const blank = (personId: string, p: PayPerson): PersonWeek => ({
  personId,
  payType: p.payType,
  jobs: 0,
  jobPayCents: 0,
  tipCents: 0,
  heldCents: 0,
  hourlyPayCents: null,
  minutes: 0,
  active: p.active,
  ownCents: 0,
});

/** A paid week as it was paid, from its snapshot alone. */
function frozen(run: PaidRun): Omit<PayWeekView, "start" | "end" | "payday" | "paid"> {
  const lines = new Map<string, PayLine>();
  const line = (pid: string) => {
    let l = lines.get(pid);
    if (!l) {
      l = { ...blank(pid, personOf(run.settings, pid)), carryCents: 0, carryFrom: null, lateCents: 0, totalCents: 0 };
      lines.set(pid, l);
    }
    return l;
  };
  for (const j of run.jobs) {
    for (const s of j.shares) {
      const l = line(s.personId);
      if (j.baseCents !== 0) l.jobs += 1;
      l.jobPayCents += s.payCents;
      l.tipCents += s.tipCents;
      l.heldCents += s.heldCents;
      l.ownCents += s.payCents + s.tipCents;
    }
  }
  for (const [pid, c] of Object.entries(run.hours)) {
    const l = line(pid);
    l.hourlyPayCents = c;
    l.ownCents += c;
  }
  for (const [pid, m] of Object.entries(run.minutes)) line(pid).minutes = m;
  for (const l of lines.values()) l.totalCents = l.ownCents;
  for (const r of run.late) {
    const l = line(r.personId);
    l.lateCents += rowCents(r);
    l.totalCents += rowCents(r);
  }
  for (const [pid, o] of Object.entries(run.owed)) {
    const l = line(pid);
    l.carryCents = o.cents;
    l.carryFrom = o.from;
    l.totalCents += o.cents;
  }
  const people = [...lines.values()];
  return {
    people,
    jobs: run.jobs,
    late: run.late,
    noCrew: [],
    totalCents: people.reduce((a, p) => a + p.totalCents, 0),
  };
}

// My pay ---------------------------------------------------------------------------------------------------------

/** paid: marked paid. owes: marked paid while below zero, so nothing was paid and it carries. open: past, not paid yet. */
export type MyPayState = "paid" | "owes" | "open" | "current";

export interface MyPayWeek {
  start: string;
  end: string;
  payday: string;
  state: MyPayState;
  amountCents: number;
  paidAt: string | null;
}

/** One line of a person's week. Their own share only: never the visit's price or anyone else's pay. */
export interface MyPayLine {
  kind: "job" | "late" | "change" | "hours";
  /** For rows from a paid week: the week it came from. */
  from: string | null;
  date: string | null;
  clientName: string | null;
  payCents: number;
  tipCents: number;
  cash: boolean;
}

export interface MyPayDetail extends MyPayWeek {
  payType: PayType;
  jobPayCents: number;
  tipCents: number;
  hourlyPayCents: number | null;
  minutes: number;
  /** Owed from earlier weeks, as a plus amount (0 when nothing). */
  owedCents: number;
  owedFrom: string | null;
  /** For a paid week: where work after it goes. */
  nextStart: string | null;
  lines: MyPayLine[];
}

const stateOf = (view: PayWeekView, today: string, cents: number): MyPayState =>
  view.paid ? (cents < 0 ? "owes" : "paid") : view.end >= today ? "current" : "open";

/** A person's week for My pay, or null when they have nothing in it. */
export function myPayDetail(view: PayWeekView, personId: string, today: string): MyPayDetail | null {
  const l = view.people.find((p) => p.personId === personId);
  if (!l) return null;
  const lines: MyPayLine[] = [];
  for (const j of view.jobs) {
    const s = j.shares.find((x) => x.personId === personId);
    if (s)
      lines.push({
        kind: "job",
        from: null,
        date: j.date,
        clientName: j.clientName,
        payCents: s.payCents,
        tipCents: s.tipCents,
        cash: j.kept,
      });
  }
  for (const r of view.late) {
    if (r.personId !== personId) continue;
    lines.push({
      kind: r.kind,
      from: r.from,
      date: r.date,
      clientName: r.clientName,
      payCents: r.payCents + r.hoursCents,
      tipCents: r.tipCents,
      cash: r.cash,
    });
  }
  return {
    start: view.start,
    end: view.end,
    payday: view.payday,
    state: stateOf(view, today, l.totalCents),
    amountCents: l.totalCents,
    paidAt: view.paid?.paidAt ?? null,
    payType: l.payType,
    jobPayCents: l.jobPayCents,
    tipCents: l.tipCents,
    hourlyPayCents: l.hourlyPayCents,
    minutes: l.minutes,
    owedCents: -Math.min(0, l.carryCents),
    owedFrom: l.carryFrom,
    nextStart: view.paid?.nextStart ?? null,
    lines,
  };
}

/**
 * Every week a person has pay in, newest first: back 26 weeks, plus the week after this one when this one was paid early
 * and work paid later already landed there.
 */
export function myPayWeeks(pay: Payroll, personId: string, today: string, weeks = 26): MyPayWeek[] {
  const out: MyPayWeek[] = [];
  const cur = periodOf(today, pay.cal);
  const ahead = nextPeriod(cur, pay.cal);
  const oldest = addDays(cur, -7 * (weeks - 1));
  const pick = (start: string, needLate: boolean) => {
    const view = pay.week(start);
    const l = view.people.find((p) => p.personId === personId);
    if (!l) return;
    if (needLate && l.lateCents === 0) return;
    if (!view.paid && l.jobs === 0 && l.totalCents === 0 && l.minutes === 0) return;
    out.push({
      start,
      end: view.end,
      payday: view.payday,
      state: stateOf(view, today, l.totalCents),
      amountCents: l.totalCents,
      paidAt: view.paid?.paidAt ?? null,
    });
  };
  if (!pay.book.runs.has(ahead) && pay.book.runs.has(cur)) pick(ahead, true);
  for (let s = cur; s >= oldest && s >= PAY_WEEK_ONE; s = periodOf(addDays(s, -1), pay.cal)) pick(s, false);
  return out;
}

// The spreadsheet ------------------------------------------------------------------------------------------------

const dollars = (c: number | null) => (c === null ? "" : (c / 100).toFixed(2));
const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/**
 * A week as a CSV: one row per person per job (the job's price, their share of the job pay and of the tip), the rows
 * from paid weeks, then a total row for the person with their hours. Jobs nobody is set on yet come last.
 */
export function payrollCsv(view: PayWeekView, nameOf: (personId: string) => string): string {
  const per = `${view.start} to ${view.end}`;
  const rows: string[][] = [
    ["week", "person", "date", "client", "job price", "share of job pay", "share of tip", "hours", "hourly pay", "total"],
  ];
  for (const p of view.people) {
    const name = nameOf(p.personId);
    for (const j of view.jobs) {
      const s = j.shares.find((x) => x.personId === p.personId);
      if (s)
        rows.push([per, name, j.date, j.clientName, dollars(j.baseCents), dollars(s.payCents), dollars(s.tipCents), "", "", ""]);
    }
    for (const r of view.late) {
      if (r.personId !== p.personId) continue;
      const why =
        r.kind === "late"
          ? `from the week of ${r.from}, done after it was paid`
          : r.kind === "hours"
            ? `change to hours after the week of ${r.from} was paid`
            : `change after the week of ${r.from} was paid`;
      const client = `${r.clientName ?? ""} (${why})`.trim();
      rows.push([per, name, r.date ?? "", client, "", dollars(r.payCents + r.hoursCents), dollars(r.tipCents), "", "", ""]);
    }
    if (p.carryCents !== 0)
      rows.push([per, name, "", `owed from the week of ${p.carryFrom ?? ""}`, "", dollars(p.carryCents), "", "", "", ""]);
    rows.push([
      per,
      name,
      "Total",
      `${String(p.jobs)} ${p.jobs === 1 ? "job" : "jobs"}`,
      "",
      dollars(p.jobPayCents),
      dollars(p.tipCents),
      (p.minutes / 60).toFixed(2),
      dollars(p.hourlyPayCents),
      dollars(p.totalCents),
    ]);
  }
  for (const j of view.noCrew)
    rows.push([per, "Nobody set yet", j.date, j.clientName, dollars(j.baseCents), "", dollars(j.tipCents), "", "", ""]);
  return `${rows.map((r) => r.map(csvCell).join(",")).join("\n")}\n`;
}
